'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { PropertyDetailListing } from '../types';
import type { ApartmentReviews, BuildingReviewsSnapshot } from '@/src/lib/api/endpoints/apartmentReviews';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import type { HotpadsClickstreamListing } from '@/src/analytics/hotpads/types';
import { useApartmentReviewsState } from '@/src/lib/hooks/query/useApartmentReviews';

const ReviewsContext = createContext<{
  listing: HotpadsClickstreamListing | null;
  propertyListing: PropertyDetailListing | null;
  lotId: string;
  reviews: ApartmentReviews | null;
  isLoading: boolean;
  googlePlaceId?: string;
  redditTopics: NonNullable<ApartmentReviews['reddit']>['topics'];
}>({
  listing: null,
  propertyListing: null,
  lotId: '',
  isLoading: false,
  reviews: null,
  redditTopics: [],
});

export function ApartmentReviewsProvider({
  listing,
  snapshot,
  children,
}: {
  listing: PropertyDetailListing;
  snapshot?: BuildingReviewsSnapshot;
  children: ReactNode;
}) {
  const { reviews, isLoading } = useApartmentReviewsState(listing, snapshot);
  const googlePlaceId = reviews?.google?.placeId;
  const redditTopics = reviews?.reddit?.topics ?? [];
  const lotId = listing.maloneLotIdEncoded ?? '';
  return (
    <ReviewsContext.Provider
      value={{
        propertyListing: listing,
        listing: toHotpadsClickstreamListing(listing),
        lotId,
        reviews,
        isLoading,
        googlePlaceId,
        redditTopics,
      }}
    >
      {children}
    </ReviewsContext.Provider>
  );
}

export const useApartmentReviewsContext = () => useContext(ReviewsContext);
